import React, { useState, useEffect } from 'react';
import { Button, Typography, Box, Paper, CircularProgress, Alert } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../../styles/Theme.js';
import DownloadIcon from '@mui/icons-material/Download';
import { Divider } from '@mui/material';
import { api } from '../../services/api';
import ReactMarkdown from 'react-markdown';
import { useCallback } from 'react';
import ShareResultsButton from './ShareResultsButton';


function Step4FinalReview({ userData, onComplete, readOnly }) {
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [responsesAnalysis, setResponsesAnalysis] = useState({});
  const [visibleSections, setVisibleSections] = useState({});
  const [visibleQuestions, setVisibleQuestions] = useState({});
  const [error, setError] = useState(null);
  const [downloadableData, setDownloadableData] = useState(null);

  // Verify if all data is present and loaded
  const isDataComplete = useCallback(() => {
    return (
      userData && 
      userData.researchID && 
      userData.surveyQuestions && 
      userData.surveyQuestions.length > 0 &&
      userData.selectedPersonas && 
      Object.keys(userData.selectedPersonas).length > 0 &&
      userData.surveyResponses && 
      userData.surveyResponses.length > 0
    );
  }, [userData]);

  // Load any missing data when component mounts
  useEffect(() => {
    const loadMissingData = async () => {
      // If readOnly, we rely on the passed userData being complete enough.
      // We skip fetching from authenticated endpoints.
      if (readOnly) {
        setDownloadableData(userData);
        return;
      }

      if (!userData || !userData.researchID) {
        setError("Research data is not available. Please return to previous steps.");
        return;
      }

      if (!isDataComplete()) {
        try {
          setDataLoading(true);
          setError(null);
          
          // Fetch the complete research data
          const research = await api.getPreviousResearch(userData.userID, userData.researchID);
          
          // Check if we need to fetch personas
          if (!userData.selectedPersonas || Object.keys(userData.selectedPersonas).length === 0) {
            const personas = await api.getPreviousPersonas(userData.userID, userData.researchID);
            if (!personas || Object.keys(personas).length === 0) {
              setError("Persona data is missing. Please return to the Personas step.");
            }
            setDownloadableData(prev => ({ ...prev, selectedPersonas: personas }));
          }
          
          // Check if we need to fetch responses
          if (!userData.surveyResponses || userData.surveyResponses.length === 0) {
            const responses = await api.getExistingResponses(userData.userID, userData.researchID);
            if (!responses || !responses.responses) {
              setError("Response data is missing. Please return to the Responses step.");
            } else {
              // Transform responses format for the download
              const transformedResponses = Object.entries(responses.responses || {}).flatMap(
                ([personaID, questionMap]) => 
                  Object.entries(questionMap).map(([questionID, response]) => ({
                    personaID,
                    questionID,
                    response,
                    researchID: userData.researchID,
                  }))
              );
              setDownloadableData(prev => ({ ...prev, surveyResponses: transformedResponses }));
            }
          }
        } catch (err) {
          console.error("Error loading data:", err);
          setError("Failed to load complete research data. Please try again.");
        } finally {
          setDataLoading(false);
        }
      } else {
        // Data is already complete
        setDownloadableData(userData);
      }
    };

    loadMissingData();
  }, [userData, isDataComplete, readOnly]);

  const fetchSavedSurveyAnalysis = useCallback(async () => {
    if (readOnly) {
      // In readOnly mode, check if analysis is already in userData
      if (userData.surveyAnalysis) {
         setAnalysisResult(userData.surveyAnalysis);
         userData.surveyAnalysis.content?.sections?.forEach((_, index) => {
          setTimeout(() => {
            setVisibleSections(prev => ({ ...prev, [index]: true }));
          }, index * 500);
        });
      }
      return;
    }

    try {
      const res = await api.getSavedAnalysis(userData.userID, userData.researchID);
      if (res && res.survey?.content?.sections) {
        setAnalysisResult(res.survey);
        res.survey.content.sections.forEach((_, index) => {
          setTimeout(() => {
            setVisibleSections(prev => ({ ...prev, [index]: true }));
          }, index * 500);
        });
      }
    } catch (err) {
      console.warn("No saved analysis found.");
    }
  }, [userData, readOnly]);

  const fetchSavedQuestionAnalyses = useCallback(async () => {
    if (readOnly) {
      // In readOnly mode, check if question analysis is already in userData
      if (userData.questionAnalysis) {
        setResponsesAnalysis(userData.questionAnalysis);
        const visibleMap = {};
        Object.keys(userData.questionAnalysis).forEach(qid => {
          visibleMap[qid] = true;
        });
        setVisibleQuestions(visibleMap);
      }
      return;
    }

    try {
      const result = await api.getSavedQuestionAnalyses(userData.userID, userData.researchID);
      setResponsesAnalysis(result || {});
      const visibleMap = {};
      Object.keys(result || {}).forEach(qid => {
        visibleMap[qid] = true;
      });
      setVisibleQuestions(visibleMap);
    } catch (err) {
      // console.warn("No saved question analyses found.");
    }
  }, [userData, readOnly]);

  useEffect(() => {
    fetchSavedSurveyAnalysis();
    fetchSavedQuestionAnalyses();
  }, [fetchSavedQuestionAnalyses, fetchSavedSurveyAnalysis, userData]);

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(',')];
    
    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        if (Array.isArray(value)) {
          return `"${value.join('; ')}"`;
        } else if (typeof value === 'object' && value !== null) {
          return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
        } else if (typeof value === 'string') {
          return `"${value.replace(/"/g, '""')}"`;
        } else {
          return value;
        }
      });
      csvRows.push(values.join(','));
    });
    
    return csvRows.join('\n');
  };

  const downloadCSV = (data, filename) => {
    const csvContent = convertToCSV(data);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  const handleDownloadResponses = () => {
    const dataToDownload = downloadableData || userData;
    
    // Create a JSON object with the research data
    const researchData = {
      researchID: dataToDownload.researchID,
      description: dataToDownload.description,
      title: dataToDownload.title,
      surveyDescription: dataToDownload.surveyDescription,
      questions: dataToDownload.surveyQuestions,
      personas: dataToDownload.selectedPersonas,
      responses: dataToDownload.surveyResponses
    };
    
    // Convert the data to a JSON string
    const jsonData = JSON.stringify(researchData, null, 2);
    
    // Create a blob with the JSON data
    const blob = new Blob([jsonData], { type: 'application/json' });
    
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.download = `research_${dataToDownload.researchID}.json`;
    
    // Append the link to the body
    document.body.appendChild(link);
    
    // Click the link to trigger the download
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPersonasCSV = () => {
    const dataToDownload = downloadableData || userData;
    if (!dataToDownload.selectedPersonas) return;
    
    const personasArray = Object.entries(dataToDownload.selectedPersonas).map(([id, persona]) => ({
      personaID: id,
      ...persona
    }));
    downloadCSV(personasArray, `personas_${dataToDownload.researchID}.csv`);
  };

  const handleDownloadSurveyCSV = () => {
    const dataToDownload = downloadableData || userData;
    if (!dataToDownload.surveyQuestions) return;
    
    const questionsArray = dataToDownload.surveyQuestions.map(question => ({
      questionID: question.questionID,
      questionText: question.questionText,
      questionType: question.questionType,
      responseOptions: Array.isArray(question.responseOptions) ? question.responseOptions.join('; ') : '',
      selectMin: question.selectMin,
      selectMax: question.selectMax
    }));
    downloadCSV(questionsArray, `survey_${dataToDownload.researchID}.csv`);
  };

  const handleDownloadResponsesCSV = () => {
    const dataToDownload = downloadableData || userData;
    if (!dataToDownload.surveyResponses) return;
    
    // Transform responses to a more CSV-friendly format
    const flattenedResponses = [];
    
    dataToDownload.surveyResponses.forEach(response => {
      flattenedResponses.push({
        personaID: response.personaID,
        questionID: response.questionID,
        response: Array.isArray(response.response) ? response.response.join('; ') : response.response,
        researchID: response.researchID
      });
    });
    
    downloadCSV(flattenedResponses, `responses_${dataToDownload.researchID}.csv`);
  };

  const handleGenerateAllAnalyses = async () => {
    if (readOnly) return;
    try {
      setLoading(true);
      setError(null);
      setVisibleSections({});
      setVisibleQuestions({});

      // Prepare questions data for analysis (MC and open-ended)
      const questions = userData.surveyQuestions.map(question => {
        const responses = userData.surveyResponses.filter(r => r.questionID === question.questionID);
        if (question.questionType === 'multiple-choice') {
          const personas = userData.selectedPersonas;
          const attributes = ["income", "gender", "age_range", "employment_status", "household_situation", "education", "marital_status"];
          const totalCounts = {};
          responses.forEach(({ response }) => {
            const values = Array.isArray(response) ? response : [response];
            values.forEach(v => {
              totalCounts[v] = (totalCounts[v] || 0) + 1;
            });
          });
          const attributeGroups = {};
          attributes.forEach(attr => {
            attributeGroups[attr] = {};
          });
          responses.forEach(({ response, personaID }) => {
            const persona = personas[personaID];
            const values = Array.isArray(response) ? response : [response];
            values.forEach(v => {
              attributes.forEach(attr => {
                const attrValue = persona?.[attr] || 'Unknown';
                if (!attributeGroups[attr][v]) {
                  attributeGroups[attr][v] = {};
                }
                attributeGroups[attr][v][attrValue] = (attributeGroups[attr][v][attrValue] || 0) + 1;
              });
            });
          });
          const flatGrouped = [];
          Object.entries(attributeGroups).forEach(([attribute, groups]) => {
            Object.entries(groups).forEach(([option, attrCounts]) => {
              flatGrouped.push({
                option,
                attribute,
                ...attrCounts
              });
            });
          });
          return {
            kind: "multiple-choice",
            questionID: question.questionID,
            totalCounts,
            groupedData: flatGrouped
          };
        } else if (question.questionType === 'open-ended') {
          const openResponses = responses.map(r => ({
            response: r.response,
            persona_id: r.personaID
          }));
          return {
            kind: "open-ended",
            questionID: question.questionID,
            responses: openResponses,
            question: question.questionText || question.prompt || ''
          };
        }
        return null;
      }).filter(Boolean);

      // Start the full analysis
      await api.generateFullAnalysis(
        userData.userID,
        userData.researchID,
        userData.description,
        questions
      );

      // Wait for completion
      const result = await api.waitForAnalysisCompletion(
        userData.userID,
        userData.researchID
      );

      // Handle the results
      if (result.survey) {
        setAnalysisResult(result.survey);
        result.survey.content?.sections?.forEach((_, index) => {
          setTimeout(() => {
            setVisibleSections(prev => ({ ...prev, [index]: true }));
          }, index * 500);
        });
      }

      if (result.questions) {
        setResponsesAnalysis(result.questions);
        const visibleMap = {};
        Object.keys(result.questions).forEach(qid => {
          visibleMap[qid] = true;
        });
        setVisibleQuestions(visibleMap);
      }

    } catch (error) {
      // console.error("Analysis generation failed", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Keep ErrorMessage component
  const ErrorMessage = () => {
    if (!error) return null;

    return (
      <Typography color="error" sx={{ mb: 2 }}>
        Error: {error}
      </Typography>
    );
  };

  if (dataLoading) {
    return (
      <ThemeProvider theme={ResearchTheme}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
          <CircularProgress size={40} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading research data...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please wait while we load your research data.
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={ResearchTheme}>
      <Box data-tabpanel="review" sx={{ width: '100%', p: 3 }}>
        <ErrorMessage />
        <Typography variant="h5" gutterBottom>
          Final Review
        </Typography>
        
        {/* Only show this Paper if there is an error to show OR if we are NOT in readOnly mode (to show summary/buttons) */}
        {(error || !readOnly) && (
          <Paper sx={{ p: 3, mb: 3 }}>
            {error && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            {!readOnly && (
              <>
                <Typography variant="h6" gutterBottom>
                  Research Summary
                </Typography>
                <Typography variant="body1" paragraph>
                  Your research "{userData.title}" is complete. You have collected responses from {Object.keys(userData.selectedPersonas || {}).length} personas for {userData.surveyQuestions?.length || 0} questions.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadResponses}
                    disabled={!(downloadableData || isDataComplete())}
                  >
                    Download All (JSON)
                  </Button>

                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadPersonasCSV}
                    disabled={!(downloadableData?.selectedPersonas || userData.selectedPersonas)}
                  >
                    Download Personas (CSV)
                  </Button>

                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadSurveyCSV}
                    disabled={!(downloadableData?.surveyQuestions || userData.surveyQuestions)}
                  >
                    Download Survey (CSV)
                  </Button>

                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadResponsesCSV}
                    disabled={!(downloadableData?.surveyResponses || userData.surveyResponses)}
                  >
                    Download Responses (CSV)
                  </Button>
                </Box>

                {/* share button on a new line - Hidden in readOnly mode */}
                <Box sx={{ mt: 2 }}>
                  <ShareResultsButton
                    userData={userData}
                    analysisData={{
                      surveyAnalysis: analysisResult,
                      questionAnalysis: responsesAnalysis
                    }}
                    disabled={!(downloadableData || isDataComplete())}
                  />
                </Box>
              </>
            )}
          </Paper>
        )}

        {/* Display message in readOnly mode if no analysis data is present */}
        {readOnly && !analysisResult && Object.keys(responsesAnalysis).length === 0 && (
          <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 1 }}>
            No analysis data available.
          </Typography>
        )}

        {!analysisResult && Object.keys(responsesAnalysis).length === 0 && !readOnly && (
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateAllAnalyses}
                disabled={loading}
              >
                {loading ? 'Generating Full Analysis...' : 'Generate Analysis'}
              </Button>
            </Box>
          )}

        {analysisResult && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Survey-Level Analysis</Typography>
          <Divider sx={{ my: 2 }} />

            <Box sx={{ mt: 2 }}>
              {analysisResult.content.sections.map((section, i) => (
                <Box 
                  key={i} 
                  sx={{ 
                    pr: 3,
                    pl: 3,
                    pt: 3,
                    opacity: visibleSections[i] ? 1 : 0,
                    transform: visibleSections[i] ? 'translateY(0)' : 'translateY(10px)',
                    transition: 'opacity 0.5s ease, transform 0.5s ease',
                  }}
                >
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    mb: 3, 
                    backgroundColor: 'var(--primary-color)', 
                    borderRadius: '8px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.6)',
                    position: 'relative',
                    overflow: 'hidden',
                    padding: '8px 16px',
                    width: 'fit-content',
                    color: 'var(--text-color)'
                    }}>
                    {section.heading}
                  </Typography>

                  {section.paragraphs.map((para, pIdx) => (
                    <ReactMarkdown
                      key={pIdx}
                      components={{
                        p: ({ node, ...props }) => (
                          <Typography variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.6, pl: 2, mb:2 }} {...props} />
                        ),
                        strong: ({ node, ...props }) => (
                          <strong style={{ fontWeight: 'bold' }} {...props} />
                        )
                      }}
                    >
                      {para}
                    </ReactMarkdown>
                  ))}

                  {section.bullets.length > 0 && (
                    <ul style={{ marginLeft: '1rem' }}>
                      {section.bullets.map((bullet, bIdx) => (
                        <li key={bIdx}>
                          <ReactMarkdown
                            components={{
                              p: ({ node, ...props }) => <span {...props} />,
                              strong: ({ node, ...props }) => (
                                <strong style={{ fontWeight: 'bold' }} {...props} />
                              ),
                            }}
                          >
                            {bullet}
                          </ReactMarkdown>
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.subsections.map((sub, sIdx) => (
                    <Box key={sIdx} sx={{ 
                    fontWeight: 500, 
                    mb: 2,
                    mr: 1,
                    ml: 1, 
                    backgroundColor: 'var(--secondary-color)', 
                    borderRadius: '8px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    padding: '8px 16px',
                    
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', pl: 1, pr: 1, pb: 2, pt: 2}}>
                        {sub.subheading}
                      </Typography>

                      <Divider sx={{ mb: 2, ml:1, mr:1,  backgroundColor: 'var(--tertiary-color)', borderWidth: 1.2 }} />

                      {sub.paragraphs.map((subPara, spIdx) => (
                        <ReactMarkdown
                          key={spIdx}
                          components={{
                            p: ({ node, ...props }) => (
                              <Typography variant="body1" sx={{ fontSize: '1.05rem',
                                lineHeight: 1.6, pl: 2, mb:2 }} {...props} />
                            ),
                            strong: ({ node, ...props }) => (
                              <strong style={{ fontWeight: 'bold' }} {...props} />
                            )
                          }}
                        >
                          {subPara}
                        </ReactMarkdown>
                      ))}

                      {sub.bullets.length > 0 && (
                        <ul style={{ marginLeft: '1rem' }}>
                          {sub.bullets.map((bullet, sbIdx) => (
                            <li key={sbIdx}>
                              <ReactMarkdown
                                components={{
                                  p: ({ node, ...props }) => <span {...props} />,
                                  strong: ({ node, ...props }) => (
                                    <strong style={{ fontWeight: 'bold' }} {...props} />
                                  ),
                                }}
                              >
                                {bullet}
                              </ReactMarkdown>
                            </li>
                          ))}

                        </ul>
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          
          <Box sx={{ textAlign: 'center', mb: 6, mt: 4 }}>
            {/* <Button 
              variant="contained"
              onClick={() => handleGenerateSurveyAnalysis(true)}
              disabled={loading || remainingAttempts <= 0}
            >
              {loading 
                ? 'Generating...' 
                : `Regenerate Survey Analysis (${remainingAttempts} attempts left)`
              }
            </Button> */}
          </Box>
        </Paper>
        )}

        {Object.keys(responsesAnalysis).length > 0 && (
        <Paper sx={{ p: 3, mb: 10 }}>
          <Typography variant="h6" gutterBottom>
            Individual Question-Level Analysis
          </Typography>
          <Divider sx={{ my: 2 }} />

          {(userData?.surveyQuestions || []).map((q, index) => {
            // Render MC question analysis
            if (q.questionType === 'multiple-choice') {
              return (
                <Box
                  key={q.questionID}
                  sx={{
                    mb: 4,
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: 'var(--primary-color)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                    opacity: visibleQuestions[q.questionID] ? 1 : 0,
                    transform: visibleQuestions[q.questionID] ? 'translateY(0)' : 'translateY(10px)',
                    transition: 'opacity 0.5s ease, transform 0.5s ease',
                    pointerEvents: visibleQuestions[q.questionID] ? 'auto' : 'none',
                    height: visibleQuestions[q.questionID] ? 'auto' : 0,
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {/* Question type indicator */}
                  <Box sx={{ position: 'absolute', bottom: 10, right: 4, zIndex: 2 }}>
                    <Typography variant="caption" sx={{
                      backgroundColor: '#e0e0e0',
                      color: '#333',
                      px: 1,
                      py: 0.2,
                      borderRadius: '6px',
                      fontWeight: 600,
                      fontSize: '0.68rem',
                      letterSpacing: 0.5,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                      mr: 1,
                    }}>
                      Multiple-Choice
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      backgroundColor: 'var(--primary-color)',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      color: 'var(--text-color)',
                      width: 'fit-content',
                    }}
                  >
                    Q{index + 1}: {q.questionText}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    {responsesAnalysis[q.questionID]?.analysis_text ? (
                      <ReactMarkdown
                        components={{
                          p: ({ node, ...props }) => (
                            <Typography variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.6, pl: 2, mb:2 }} {...props} />
                          ),
                          strong: ({ node, ...props }) => (
                            <strong style={{ fontWeight: 'bold' }} {...props} />
                          )
                        }}
                      >
                        {responsesAnalysis[q.questionID].analysis_text}
                      </ReactMarkdown>
                    ) : (
                      <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2, pl: 2 }}>
                        No analysis generated yet.
                      </Typography>
                    )}

                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                      {/* <Button
                        variant="contained"
                        onClick={() => handleGenerateResponseAnalysis(q.questionID, true)}
                        disabled={loading || remainingAttempts <= 0}
                       
                      >
                        {loading
                          ? 'Generating...'
                          : `Regenerate Analysis (${remainingAttempts} attempts left)`
                        }
                      </Button> */}
                    </Box>
                  </Box>
                </Box>
              );
            }
            // Render open-ended question analysis
            if (q.questionType === 'open-ended') {
              return (
                <Box
                  key={q.questionID}
                  sx={{
                    mb: 4,
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: '#fff',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                    opacity: visibleQuestions[q.questionID] ? 1 : 0,
                    transform: visibleQuestions[q.questionID] ? 'translateY(0)' : 'translateY(10px)',
                    transition: 'opacity 0.5s ease, transform 0.5s ease',
                    pointerEvents: visibleQuestions[q.questionID] ? 'auto' : 'none',
                    height: visibleQuestions[q.questionID] ? 'auto' : 0,
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {/* Question type indicator */}
                  <Box sx={{ position: 'absolute', bottom: 10, right: 4, zIndex: 2 }}>
                    <Typography variant="caption" sx={{
                      backgroundColor: '#e0e0e0',
                      color: '#333',
                      px: 1,
                      py: 0.2,
                      borderRadius: '6px',
                      fontWeight: 600,
                      fontSize: '0.68rem',
                      letterSpacing: 0.5,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                      mr: 1,
                    }}>
                      Open-Ended
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      color: 'var(--text-color)',
                      width: 'fit-content',
                    }}
                  >
                    Q{index + 1}: {q.questionText}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    {responsesAnalysis[q.questionID]?.analysis_text ? (
                      <ReactMarkdown
                        components={{
                          p: ({ node, ...props }) => (
                            <Typography variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.6, pl: 2, mb:2 }} {...props} />
                          ),
                          strong: ({ node, ...props }) => (
                            <strong style={{ fontWeight: 'bold' }} {...props} />
                          )
                        }}
                      >
                        {responsesAnalysis[q.questionID].analysis_text}
                      </ReactMarkdown>
                    ) : (
                      <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2, pl: 2 }}>
                        No analysis generated yet.
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }
            return null;
          })}
        </Paper>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default Step4FinalReview;